import { useRef } from "react"
import {
	IconLink3Outline18 as Link3,
	IconCloudUploadOutline18 as Upload,
} from "nucleo-ui-outline-18"

import type { SegmentedControlOption } from "@productlane/ui"
import { MAX_SPEC_BYTES } from "@productlane/lib/src/api-reference/types"
import { Button, SegmentedControl } from "@productlane/ui"

export type SpecSourceKind = "url" | "file"

const SOURCE_KINDS: readonly SegmentedControlOption<SpecSourceKind>[] = [
	{ value: "url", label: "URL", Icon: Link3 },
	{ value: "file", label: "File", Icon: Upload },
]

export const SPEC_TOO_LARGE = "The spec is larger than 20 MB."

export function SpecSourceKindControl({
	name,
	value,
	onValueChange,
}: {
	name: string
	value: SpecSourceKind
	onValueChange: (value: SpecSourceKind) => void
}) {
	return (
		<SegmentedControl
			name={name}
			value={value}
			options={SOURCE_KINDS}
			onValueChange={onValueChange}
			showLabels
			className="[&_button]:gap-1.5 [&_button]:px-2.5"
		/>
	)
}

/** A file button with the size limit beside it, so a too-large spec is caught before it uploads. */
export function SpecFileButton({
	label,
	fileName,
	isLoading,
	onFile,
}: {
	label: string
	fileName?: string | null
	isLoading?: boolean
	onFile: (file: File) => void
}) {
	const inputRef = useRef<HTMLInputElement>(null)
	return (
		<div className="flex min-w-0 items-center gap-2.5">
			<input
				ref={inputRef}
				type="file"
				accept=".json,.yaml,.yml,application/json,application/yaml"
				className="hidden"
				onChange={(e) => {
					const file = e.target.files?.[0]
					if (file) onFile(file)
					e.target.value = ""
				}}
			/>
			<span className="min-w-0 truncate text-sm text-tertiary">
				{fileName ?? "Up to 20 MB"}
			</span>
			<Button
				variant="outline"
				size="sm"
				Icon={Upload}
				isLoading={isLoading}
				onClick={() => inputRef.current?.click()}
				className="flex-none"
			>
				{label}
			</Button>
		</div>
	)
}

/** Upload straight to storage: specs run to several MB, past what a JSON request body takes. */
export async function uploadSpecFile(
	file: File,
	getUploadUrl: (input: {
		fileName: string
		size: number
	}) => Promise<{ url: string; uploadKey: string }>,
): Promise<{ fileName: string; uploadKey: string }> {
	if (file.size > MAX_SPEC_BYTES) throw new Error(SPEC_TOO_LARGE)
	const { url, uploadKey } = await getUploadUrl({
		fileName: file.name,
		size: file.size,
	})
	const response = await fetch(url, {
		method: "PUT",
		body: file,
		headers: {
			"Content-Type": /\.ya?ml$/i.test(file.name)
				? "application/yaml"
				: "application/json",
		},
	})
	if (!response.ok) throw new Error("The upload didn't go through. Try again.")
	return { fileName: file.name, uploadKey }
}
